import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { 
  LayoutDashboard, 
  LogOut, 
  Menu, 
  X,
  School,
  MicVocal,
  AudioWaveform,
  ListChecks,
  Trophy,
  ChevronRight,
  TrendingUp,
  Maximize,
  Minimize,
  AudioLines,
  CircleUser,
  MonitorPlay,
  Clapperboard,
  ZoomIn,
  ZoomOut,
  Library,
  Palette,
  UsersRound,
  UserCheck,
  Medal,
  BrainCircuit,
  Shield,
  Gamepad2,
  PenLine,
  Database
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

import LicenseWarningBanner from '../components/LicenseWarningBanner';

// VocaLab Icon Component
const VocaLabIcon = ({ className = "h-10 w-10" }) => (
  <img 
    src="/vocalab-icon.png"
    alt="VocaLab"
    className={`${className} vocalab-icon rounded-xl`}
  />
);

const DashboardLayout = ({ children }) => {
  const { user, school, logout, isAdmin, isTeacher, isStudent } = useAuth();
  const { theme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100); // percent
  const location = useLocation();
  const navigate = useNavigate();

  // Check fullscreen state and handle keyboard shortcuts
  useEffect(() => {
    const handleFullscreenChange = () => {
      // Check all vendor-prefixed fullscreen states
      setIsFullscreen(!!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      ));
    };

    // Handle CTRL+ENTER to toggle fullscreen (for all users)
    const handleKeyDown = (e) => {
      // CTRL + ENTER to toggle fullscreen
      if (e.ctrlKey && e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        
        const isCurrentlyFullscreen = !!(
          document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.msFullscreenElement
        );

        if (!isCurrentlyFullscreen) {
          // Enter fullscreen
          const elem = document.documentElement;
          if (elem.requestFullscreen) {
            elem.requestFullscreen().catch(() => {});
          } else if (elem.webkitRequestFullscreen) {
            elem.webkitRequestFullscreen();
          } else if (elem.msRequestFullscreen) {
            elem.msRequestFullscreen();
          }
        } else {
          // Exit fullscreen
          if (document.exitFullscreen) {
            document.exitFullscreen().catch(() => {});
          } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen();
          } else if (document.msExitFullscreen) {
            document.msExitFullscreen();
          }
        }
      }
      
      // Block ESC from exiting fullscreen (only for document-level fullscreen, not video players)
      if (e.key === 'Escape') {
        const isCurrentlyFullscreen = !!(
          document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.msFullscreenElement
        );
        
        // Only block ESC if we're in document fullscreen and NOT inside a video element
        const activeElement = document.activeElement;
        const isVideoElement = activeElement && (
          activeElement.tagName === 'VIDEO' ||
          activeElement.closest('video') ||
          activeElement.closest('[data-video-player]')
        );
        
        if (isCurrentlyFullscreen && !isVideoElement) {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);
    document.addEventListener('keydown', handleKeyDown, true); // Use capture phase

    // Initial check
    handleFullscreenChange();

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, []);

  const toggleFullscreen = () => {
    // Check actual fullscreen state at click time (not React state which may be stale)
    const isCurrentlyFullscreen = !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.msFullscreenElement
    );

    if (!isCurrentlyFullscreen) {
      // Enter fullscreen
      const elem = document.documentElement;
      if (elem.requestFullscreen) {
        elem.requestFullscreen().catch(err => {
          console.error('Failed to enter fullscreen:', err);
        });
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      }
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(err => {
          console.error('Failed to exit fullscreen:', err);
        });
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };

  // Track fullscreen state before page unload (for refresh persistence)
  useEffect(() => {
    const handleBeforeUnload = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      );
      // Save fullscreen state before page unloads
      sessionStorage.setItem('vocalab_was_fullscreen', isCurrentlyFullscreen ? '1' : '0');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Auto-enter fullscreen on login OR restore fullscreen after refresh
  useEffect(() => {
    const wasFullscreen = sessionStorage.getItem('vocalab_was_fullscreen') === '1';
    const alreadyFullscreened = sessionStorage.getItem('vocalab_fullscreen_done');
    
    // Re-enter fullscreen if user was in fullscreen before refresh, OR first time login
    const shouldEnterFullscreen = wasFullscreen || !alreadyFullscreened;
    
    if (!shouldEnterFullscreen) return;

    const timer = setTimeout(() => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      );
      if (!isCurrentlyFullscreen) {
        const elem = document.documentElement;
        if (elem.requestFullscreen) {
          elem.requestFullscreen().catch(() => {});
        } else if (elem.webkitRequestFullscreen) {
          elem.webkitRequestFullscreen();
        } else if (elem.msRequestFullscreen) {
          elem.msRequestFullscreen();
        }
      }
      sessionStorage.setItem('vocalab_fullscreen_done', '1');
    }, 800);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = () => {
    sessionStorage.removeItem('vocalab_fullscreen_done');
    sessionStorage.removeItem('vocalab_was_fullscreen');
    logout();
    navigate('/login');
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 10, 200));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 10, 50));
  };

  const handleZoomReset = () => {
    setZoomLevel(100);
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  };

  const adminLinks = [
    { href: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
    { href: '/admin/analytics', icon: TrendingUp, label: 'Analytics' },
    { href: '/admin/teachers', icon: UsersRound, label: 'Teachers' },
    { href: '/admin/classes', icon: School, label: 'Classes' },
    { href: '/admin/branding', icon: Palette, label: 'Branding' },
    { href: '/admin/voice-settings', icon: AudioLines, label: 'Voice Settings' },
    { href: '/teacher/modules', icon: BrainCircuit, label: 'Test Modules' },
    { href: '/teacher/video-lectures', icon: MonitorPlay, label: 'Video Lectures' },
    { href: '/teacher/fun-learning', icon: Gamepad2, label: 'Fun Learning' },
    { href: '/teacher/backup-restore', icon: Database, label: 'Backup & Restore' },
    { href: '/resources', icon: Library, label: 'Resources & Docs' },
    { href: '/admin/license', icon: Shield, label: 'License' },
  ];

  const teacherLinks = [
    { href: '/teacher', icon: LayoutDashboard, label: 'Dashboard' },
    { href: '/teacher/analytics', icon: TrendingUp, label: 'Analytics' },
    { href: '/teacher/students', icon: UserCheck, label: 'Students' },
    { href: '/teacher/classes', icon: School, label: 'My Classes' },
    { href: '/teacher/modules', icon: BrainCircuit, label: 'Test Modules' },
    { href: '/teacher/video-lectures', icon: MonitorPlay, label: 'Video Lectures' },
    { href: '/teacher/fun-learning', icon: Gamepad2, label: 'Fun Learning' },
    { href: '/teacher/backup-restore', icon: Database, label: 'Backup & Restore' },
    { href: '/teacher/voice-settings', icon: AudioLines, label: 'Voice Settings' },
    { href: '/teacher/profile', icon: CircleUser, label: 'My Profile' },
    { href: '/resources', icon: Library, label: 'Resources & Docs' },
  ];

  const studentLinks = [
    { href: '/student', icon: LayoutDashboard, label: 'Dashboard' },
    { href: '/student/tts', icon: AudioWaveform, label: 'Text to Speech' },
    { href: '/student/stt', icon: MicVocal, label: 'Speech to Text' },
    { href: '/student/mcq', icon: ListChecks, label: 'MCQ Test' },
    { href: '/student/ftb', icon: PenLine, label: 'Fill the Blanks' },
    { href: '/student/vmcq', icon: Clapperboard, label: 'Cinematic MCQ' },
    { href: '/student/video-lectures', icon: MonitorPlay, label: 'Video Lectures' },
    { href: '/student/fun-learning', icon: Gamepad2, label: 'Fun Learning' },
    { href: '/student/leaderboard', icon: Medal, label: 'Leaderboard' },
    { href: '/student/profile', icon: CircleUser, label: 'My Profile' },
  ];

  const links = isAdmin ? adminLinks : isTeacher ? teacherLinks : studentLinks;

  const isActive = (href) => {
    if (href === '/admin' || href === '/teacher' || href === '/student') {
      return location.pathname === href;
    }
    return location.pathname === href || location.pathname.startsWith(href + '/') || location.pathname.startsWith(href);
  };

  const isDark = theme?.isDark;

  return (
    <div 
      className="min-h-screen" 
      style={{ 
        background: isDark 
          ? `linear-gradient(135deg, ${theme.colors.backgroundColor} 0%, #581c87 50%, ${theme.colors.backgroundColor} 100%)`
          : '#f8f9fa'
      }}
    >
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 bottom-0 w-64 z-50 transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 ${isDark ? 'glass border-r border-white/10' : 'border-r border-slate-200'} flex flex-col`}
        style={!isDark ? { background: '#2c3e50' } : {}}
      >
        {/* Sidebar Header */}
        <div className={`h-20 flex items-center px-4 flex-shrink-0 ${isDark ? 'border-b border-white/10' : 'border-b border-white/10'}`}>
          <div className="flex-1 min-w-0">
            <h1 className={`text-lg font-bold truncate ${isDark ? 'premium-gradient-text' : 'text-white'}`}>
              English VocaLab
            </h1>
            <p className={`text-sm ${isDark ? 'text-violet-300' : 'text-blue-300'}`}>Powered by AI</p>
          </div>
          <button 
            className="lg:hidden p-2"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5 text-white/70" />
          </button>
        </div>

        {/* Navigation - Scrollable Area */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <nav className="p-4 space-y-0.5">
            {links.map((link) => {
              const Icon = link.icon;
              const active = isActive(link.href);
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                    active 
                      ? isDark 
                        ? 'bg-gradient-to-r from-violet-600/30 to-cyan-600/20 text-white border border-violet-500/40 shadow-lg shadow-violet-900/20' 
                        : 'bg-white/20 text-white border border-white/30 shadow-md'
                      : isDark
                        ? 'text-slate-400 hover:bg-white/5 hover:text-white'
                        : 'text-white/65 hover:bg-white/10 hover:text-white'
                  }`}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <span className={`flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 transition-all duration-200 ${
                    active
                      ? isDark
                        ? 'bg-gradient-to-br from-violet-500/40 to-cyan-500/30 text-white shadow-inner'
                        : 'bg-white/25 text-white'
                      : isDark
                        ? 'bg-white/5 text-slate-400 group-hover:bg-white/10 group-hover:text-white'
                        : 'bg-white/10 text-white/65 group-hover:bg-white/20 group-hover:text-white'
                  }`}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="font-medium text-sm">{link.label}</span>
                  {active && <ChevronRight className="h-3.5 w-3.5 ml-auto opacity-70" />}
                </Link>
              );
            })}
          </nav>

          {/* Trial/License Warning - Inside scrollable area - REMOVED */}
        </div>

        {/* User Profile & Logout - Fixed at Bottom */}
        <div className={`p-4 flex-shrink-0 ${isDark ? 'border-t border-white/10' : 'border-t border-white/10'}`}>
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 border-2 border-violet-500/30">
              <AvatarFallback className={`${isDark ? 'bg-violet-500/20 text-violet-300' : 'bg-white/20 text-white'}`}>
                {getInitials(user?.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-white'}`}>
                {user?.name}
              </p>
              <p className={`text-xs capitalize ${isDark ? 'text-slate-400' : 'text-white/60'}`}>
                {user?.role}
              </p>
            </div>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={handleLogout}
              data-testid="logout-btn"
              className={`${isDark ? 'text-slate-400 hover:text-rose-400 hover:bg-rose-500/10' : 'text-white/60 hover:text-rose-300 hover:bg-white/10'}`}
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className={`h-20 sticky top-0 z-30 ${isDark ? 'glass border-b border-white/10' : 'bg-white border-b border-slate-200 shadow-sm'}`}>
          <div className="h-full flex items-center px-4 lg:px-6 justify-between">
            {/* Left: Mobile menu + VocaLab Icon */}
            <div className="flex items-center gap-3">
              <button 
                className="lg:hidden p-2 rounded-lg hover:bg-white/10"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-btn"
              >
                <Menu className={`h-5 w-5 ${isDark ? 'text-white' : 'text-slate-600'}`} />
              </button>
              <div className="flex items-center gap-3">
                <VocaLabIcon className="h-12 w-12" />
              </div>
            </div>

            {/* Center: Main heading + School name */}
            <div className="flex-1 text-center px-4">
              <h1 className={`text-xl lg:text-2xl font-bold ${isDark ? 'premium-gradient-text' : 'text-slate-800'}`}>
                English VocaLab – Powered by AI
              </h1>
              {school?.name && school.name !== 'English VocaLab' && (
                <p className={`text-base ${isDark ? 'text-violet-300' : 'text-slate-500'}`}>
                  {school.name}
                </p>
              )}
            </div>

            {/* Right: Zoom + Fullscreen toggle + Theme Toggle + School logo */}
            <div className="flex items-center gap-2">
              {/* Zoom buttons - all users */}
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleZoomOut}
                  className={`h-8 w-8 ${isDark ? 'text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10' : 'text-slate-500 hover:text-indigo-600'}`}
                  title={`Zoom Out (${zoomLevel}%)`}
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <button
                  onClick={handleZoomReset}
                  className={`text-xs font-mono px-1 rounded ${isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
                  title="Reset Zoom"
                >
                  {zoomLevel}%
                </button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleZoomIn}
                  className={`h-8 w-8 ${isDark ? 'text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10' : 'text-slate-500 hover:text-indigo-600'}`}
                  title={`Zoom In (${zoomLevel}%)`}
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
              </div>
              {/* Fullscreen button - all users */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleFullscreen}
                data-testid="fullscreen-btn"
                className={`${isDark ? 'text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10' : 'text-slate-500 hover:text-indigo-600'}`}
                title={isFullscreen ? 'Exit Fullscreen (Ctrl+Enter)' : 'Enter Fullscreen (Ctrl+Enter)'}
              >
                {isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
              </Button>
              {school?.logo_path ? (
                <img 
                  src={`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api/school/logo/${school.logo_path}`}
                  alt="School Logo"
                  className="h-12 w-12 rounded-xl object-cover border-2 border-white/20"
                />
              ) : null}
            </div>
          </div>
        </header>

        {/* License Warning Banner */}
        <LicenseWarningBanner />

        {/* Page content */}
        <main 
          className="p-4 lg:p-6 min-h-[calc(100vh-5rem)]"
          style={zoomLevel !== 100 ? {
            transform: `scale(${zoomLevel / 100})`,
            transformOrigin: 'top left',
            width: `${10000 / zoomLevel}%`
          } : {}}
        >
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
